import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { captureApiError } from '../utils/errorCapture';
import { installStepUpInterceptor } from './stepUpInterceptor';
import { isWrongPortalError, noteWrongPortal } from '../services/wrongPortal';
import { isPlainLocalhost, localDevTenant } from '../utils/devTenant';
import { PORTAL_HOST_SUFFIX } from '../utils/portalDomain';

/**
 * API Configuration for Goodstuff Portal
 *
 * URL Structure: {tenant}.bcportal.app/{company}/{page}
 * Example: goodstuff365.bcportal.app/Cronus/dashboard
 *
 * - Tenant comes from subdomain (handled by TenantContext)
 * - Company comes from first path segment (handled by CompanyContext)
 * - API is served from same origin: {tenant}.bcportal.app/api/*
 */

/**
 * Extract tenant subdomain from hostname
 */
const getTenantSubdomain = (): string | null => {
  const hostname = window.location.hostname;

  if (hostname.endsWith(PORTAL_HOST_SUFFIX)) {
    return hostname.slice(0, -PORTAL_HOST_SUFFIX.length);
  }
  if (hostname.endsWith('.localhost')) {
    return hostname.replace('.localhost', '');
  }
  if (isPlainLocalhost(hostname)) {
    // Explicit local tenant only (utils/devTenant.ts); no silent default
    return localDevTenant();
  }
  return null;
};

/**
 * Extract company slug from URL path
 * URL format: /{company}/... (e.g., /CRONUS%20International%20Ltd./dashboard)
 */
const getCompanySlug = (): string | null => {
  const path = window.location.pathname;
  const parts = path.split('/').filter(p => p.length > 0);

  if (parts.length > 0) {
    try {
      return decodeURIComponent(parts[0]);
    } catch (_e) {
      return parts[0];
    }
  }
  return null;
};

// Get tenant and company from URL
const tenantSubdomain = getTenantSubdomain();
const companySlug = getCompanySlug();

// API base path is always /api (tenant comes from subdomain)
const API_BASE_PATH = '/api';

/**
 * Get API base URL
 * - Development: localhost:8787 (Workers dev server)
 * - Production: Same origin (subdomain serves both frontend and API)
 */
const getApiBaseUrl = (): string => {
  // Allow override via environment variable
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  // Development: Use Workers dev server
  if (import.meta.env.DEV) {
    return 'http://localhost:8787';
  }

  // Production: API is on same origin (subdomain routing)
  // goodstuff365.bcportal.app/api/* is handled by Workers
  return '';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Session transport (A3).
 *  - 'cookie': the API sets HttpOnly cookies and never hands tokens to JS. Used
 *    whenever the API is same-site with the page (production subdomains serve
 *    both; local dev is localhost:3000 → localhost:8787).
 *  - 'bearer': legacy localStorage tokens + Authorization header. Used when the
 *    API is on another site (VITE_API_URL pointing at another host),
 *    where third-party cookies would be blocked.
 * Override with VITE_AUTH_MODE=cookie|bearer.
 */
export type AuthMode = 'cookie' | 'bearer';
const resolveAuthMode = (): AuthMode => {
  const forced = import.meta.env.VITE_AUTH_MODE;
  if (forced === 'cookie' || forced === 'bearer') return forced;
  if (!API_BASE_URL) return 'cookie';
  try {
    return new URL(API_BASE_URL).hostname === window.location.hostname ? 'cookie' : 'bearer';
  } catch {
    return 'bearer';
  }
};
const AUTH_MODE: AuthMode = resolveAuthMode();
const isCookieMode = (): boolean => AUTH_MODE === 'cookie';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL ? API_BASE_URL + API_BASE_PATH : API_BASE_PATH,
  timeout: 30000,
  withCredentials: isCookieMode(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage keys (bearer mode only; cookie mode keeps nothing in storage)
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

const lsGet = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string): void => { try { localStorage.setItem(k, v); } catch { /* quota */ } };
const lsDel = (k: string): void => { try { localStorage.removeItem(k); } catch { /* ignore */ } };

/** Bearer access token, or null in cookie mode (the token lives in an HttpOnly cookie). */
const getToken = (): string | null => (isCookieMode() ? null : lsGet(TOKEN_KEY));
const setToken = (token: string): void => { if (!isCookieMode()) lsSet(TOKEN_KEY, token); };
const removeToken = (): void => { lsDel(TOKEN_KEY); csrfFromBody = null; };
const getRefreshToken = (): string | null => (isCookieMode() ? null : lsGet(REFRESH_TOKEN_KEY));
const removeRefreshToken = (): void => lsDel(REFRESH_TOKEN_KEY);

// ── CSRF (cookie mode): double-submit token ──
let csrfFromBody: string | null = null;
/** Current CSRF token: the readable bcp_csrf cookie, else the last one the API returned. */
const getCsrfToken = (): string | null => {
  try {
    const m = document.cookie.match(/(?:^|;\s*)(?:__Host-)?bcp_csrf=([^;]+)/);
    if (m) return decodeURIComponent(m[1]);
  } catch { /* no document */ }
  return csrfFromBody;
};
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Headers + credentials for code that uses fetch() instead of the axios client.
 * Bearer mode adds Authorization; cookie mode sends cookies plus the CSRF header.
 */
const authFetchInit = (method = 'GET'): { credentials: RequestCredentials; headers: Record<string, string> } => {
  const headers: Record<string, string> = {};
  const sub = getTenantSubdomain();
  if (sub) headers['X-Tenant-Subdomain'] = sub;
  if (isCookieMode()) {
    headers['X-Auth-Mode'] = 'cookie';
    const csrf = getCsrfToken();
    if (csrf && UNSAFE_METHODS.has(method.toLowerCase())) headers['X-CSRF-Token'] = csrf;
    return { credentials: 'include', headers };
  }
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return { credentials: 'same-origin', headers };
};

/**
 * Better Auth sessions (BA frontend PR): there is no refresh step any more. A session is one
 * HttpOnly cookie that Better Auth extends while it is used; a 401 means it has really ended.
 * Tokens left in localStorage by the old sign-in are wiped once (cookie mode only).
 */
let legacyWiped = false;
const wipeLegacyTokens = (): void => {
  if (legacyWiped || !isCookieMode()) return;
  legacyWiped = true;
  lsDel(TOKEN_KEY);
  lsDel(REFRESH_TOKEN_KEY);
};

// Request interceptor - Add auth (bearer token or cookie-mode headers) and tenant header
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    if (isCookieMode()) {
      wipeLegacyTokens();
      config.headers['X-Auth-Mode'] = 'cookie';
      const csrf = getCsrfToken();
      if (csrf && UNSAFE_METHODS.has((config.method || 'get').toLowerCase())) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    } else {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add tenant header for all requests (needed when API is on different domain)
    // In production the API is same-origin ({tenant}.bcportal.app/api); the header matters for dev/other hosts
    const tenantSubdomain = getTenantSubdomain();
    if (tenantSubdomain) {
      config.headers['X-Tenant-Subdomain'] = tenantSubdomain;
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
);

// Response interceptor - Handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    const csrf = (response.data as { csrfToken?: unknown } | undefined)?.csrfToken;
    if (typeof csrf === 'string') csrfFromBody = csrf;
    return response;
  },
  async (error: AxiosError): Promise<unknown> => {
    const originalRequest = error.config as InternalAxiosRequestConfig | undefined;

    // 401s from the sign-in steps themselves (wrong password / wrong code / expired pending
    // sign-in) are not "your session ended": the sign-in page shows its own message for them.
    const isSignInStep = /\/auth\/(sign-in\/|two-factor\/|passkey\/(generate-authenticate-options|verify-authentication)|magic-link\/|transfer)/
      .test(originalRequest?.url ?? '');

    if (error.response?.status === 401 && !isSignInStep) {
      // #420: a session used on another tenant's portal. Note it first (the sign-in page explains
      // it, and the app's cache still says which portal is theirs), then sign out here as usual.
      if (isWrongPortalError(error)) { noteWrongPortal(); }
      removeToken();
      removeRefreshToken();
      window.dispatchEvent(new CustomEvent('auth:failed', {
        detail: { message: 'Session expired' }
      }));
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('[API] Access forbidden:', (error.response?.data as { error?: string })?.error);
    }

    // Handle 402 Payment Required — subscription enforcement (see workers/src/middleware/subscription.ts)
    if (error.response?.status === 402) {
      const errorCode = (error.response?.data as { error?: string })?.error;
      if (errorCode === 'subscription_inactive' || errorCode === 'subscription_read_only') {
        window.dispatchEvent(new CustomEvent('subscription:denied', {
          detail: error.response.data
        }));
        // Pages and services surface `data.error` in toasts/inline errors, which would show
        // the raw code ("subscription_read_only"). Swap in the server's human message and keep
        // the machine-readable code on `data.code` (SubscriptionGate already has its copy).
        const body = error.response.data as { error?: string; message?: string; code?: string };
        if (body && typeof body.message === 'string' && body.message) {
          body.code = errorCode;
          body.error = body.message;
        }
      }
    }

    // Handle network errors
    if (!error.response) {
      console.error('[API] Network error:', error.message);
    }

    // Capture for support widget context (skip auth endpoints — request body may contain credentials)
    const captureUrl = error.config?.url ?? '';
    if (!captureUrl.includes('/auth/')) {
      const requestBody = (() => {
        try { return error.config?.data ? JSON.parse(error.config.data as string) : undefined; } catch { return error.config?.data; }
      })();
      captureApiError(
        captureUrl,
        error.config?.method ?? 'unknown',
        error.response?.status ?? null,
        requestBody,
        error.response?.data,
        error.message
      );
    }

    return Promise.reject(error);
  }
);

// A8: prompt "Confirm it's you" and retry once on 403 step_up_required
installStepUpInterceptor(api);

// Log configuration
console.log(`[API Config] Tenant: ${tenantSubdomain || 'NOT DETECTED'}`);
console.log(`[API Config] Company: ${companySlug || 'NOT SPECIFIED'}`);
console.log(`[API Config] Base URL: ${api.defaults.baseURL}`);
console.log(`[API Config] Environment: ${import.meta.env.MODE}`);

// Export utilities
/**
 * Store a session token handed to the page (bearer mode, dev only). No-op in cookie mode:
 * the API set an HttpOnly cookie and sends no token.
 */
const storeSession = (token?: string | null, _refreshToken?: string | null): void => {
  if (token) setToken(token);
};

/** True when an auth response established a session (token in body, or cookie mode). */
const isSessionResponse = (data: unknown): boolean => {
  const d = data as { token?: unknown; authMode?: unknown } | null | undefined;
  return !!d && (typeof d.token === 'string' || d.authMode === 'cookie');
};

/** Clear every trace of the session held by the client (tokens, CSRF). */
const clearSession = (): void => { removeToken(); removeRefreshToken(); };

export {
  getToken, getRefreshToken, setToken, removeToken, storeSession, clearSession, isSessionResponse,
  getCsrfToken, authFetchInit, isCookieMode, AUTH_MODE,
  tenantSubdomain, companySlug, getTenantSubdomain, getCompanySlug,
};
export default api;
